import { toast } from '@/components/ui/toast/use-toast';
import { store } from '@/store/store';
import axios from 'axios';
import { useFieldArray } from 'vee-validate';
import { computed, ref } from 'vue';

export interface UploadedFile {
    name: string;
    id: string;
}

export interface Params {
    appID?: string;
    patientId?: string
}

export function useFileUpload(params: Params = {}) {
    const fileLoader = ref(false);

    const getAuthorization = () => {
        const token = localStorage.getItem('token');
        return token ? `Bearer ${token}` : '';
    };

    const uploadedFiles = useFieldArray<UploadedFile>('uploadedFiles');
    const fileNames = ref<string[]>([]);

    const files = computed(() => uploadedFiles.fields.value);

    const uploadFile = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const uploaded = Array.from(target?.files || []);
        if (!uploaded.length || fileLoader.value) {
            return;
        }

        fileLoader.value = true;

        const formData = new FormData();
        const names: string[] = [];
        uploaded.forEach((file) => {
            formData.append('documents[]', file);
            names.push(file.name);
        });
        if (params.appID) {
            formData.append('appID', params.appID);
        }
        // use provided patient ID or fallback to appointment PID
        const pid = params.patientId || store.appointment.pid;
        if (pid) {
            formData.append('pid', pid);
        }

        try {
            const res: any = await axios.post('/fileTransfer/newAppointment', formData, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Authorization": getAuthorization()
                }
            });

            if (res.status === 'success') {
                names.forEach((fileName, index) => {
                    uploadedFiles.push({
                        name: fileName,
                        id: res.data.files[index] as string
                    });
                });
                fileNames.value = names;
            } else {
                toast({
                    title: res.msg,
                    variant: 'destructive'
                });
            }
        } catch (error: any) {
            toast({
                title: error.response?.data?.msg || 'Failed to upload file',
                variant: 'destructive'
            });
        } finally {
            fileLoader.value = false;
            target.value = '';
        }
    };

    const removeFile = (index: number) => {
        uploadedFiles.remove(index);
    };

    return {
        files,
        fileNames,
        fileLoader,
        uploadFile,
        removeFile
    };
}
